import React, { useState, useEffect } from 'react';
import { MdDelete, MdShoppingCart } from "react-icons/md";
import { Link } from 'react-router-dom';
import CartServices from '../services/API/CartService';
import axios from 'axios';
import { FaArrowRight } from 'react-icons/fa';

const Main = () => {
    const [cartProducts, setCartProducts] = useState([]);
    const [cartProductsTotal, setCartProductsTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const userId = sessionStorage.getItem("userId") || sessionStorage.getItem("randomUserId");

    const getCartData = async (userId) => {
        try {
            const res = await CartServices.getCart(userId);
            if (res.status) {
                const products = res.data.order;
                setCartProducts(products);
                const total = products.reduce((acc, item) => acc + parseFloat(item.total_price), 0);
                setCartProductsTotal(total.toFixed(2));
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error fetching cart data:", error);
        }
    };
    const updateQuantity = async (productId, newQuantity) => {
        setCartProducts(prevCart => {
            const updatedCart = prevCart.map(item => 
                item.id === productId ? { ...item, quantity: newQuantity, total_price: (newQuantity * parseFloat(item.products.price)).toFixed(2) } : item
            );
    
            // Update total price dynamically
            const newTotal = updatedCart.reduce((acc, item) => acc + parseFloat(item.total_price), 0);
            setCartProductsTotal(newTotal.toFixed(2));
    
            return updatedCart;
        });
    
        const payload = {
            cart_id: productId,
            quantity: newQuantity,
            user_id: userId,
        };
    
        try {
            const response = await CartServices.updateCart(payload);
            if (!response.status) {
                console.error("Failed to update cart");
            }
        } catch (error) {
            console.error("Error updating cart:", error);
        }
    };
    
    const increment = (productId, currentQuantity) => {
        const newQuantity = currentQuantity + 1;
        updateQuantity(productId, newQuantity);
    };
    
    const decrement = (productId, currentQuantity) => {
        if (currentQuantity > 1) {
            const newQuantity = currentQuantity - 1;
            updateQuantity(productId, newQuantity);
        }
    };
    
    useEffect(() => {
        getCartData(userId);
    }, [userId]);

    const handleDeleteCartById = async (cartId) => {
        const payload = {
            user_id: userId,
            cart_id: cartId,
          };
        try {
            const response = await CartServices.deleteCartById(payload);
            console.log("Product removed from cart successfully:", response.data);
            getCartData(userId); // Fetch updated cart data
            cartProducts([]); // Set empty cart explicitly

        } catch (error) {
            console.error("Error removing product from cart:", error);
        }
    };

    const handleDeleteCart = async () => {
        try {
            const response = await CartServices.deleteCart(userId);
            if (response.status) {
                console.log("Cart cleared successfully");
                getCartData(userId); // Fetch updated cart data
                cartProducts([]); // Set empty cart explicitly
            } else {
                console.error("Failed to clear cart");
            }
        } catch (error) {
            console.error("Error clearing cart:", error);
        }
    };
    if (isLoading) {
        return <div className="flex justify-center items-center py-16 bg-[#fff]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#F4CF38]"></div>
      </div>;
    }

    if (cartProducts.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-screen text-center px-4">
                <MdShoppingCart className="text-6xl text-gray-400" />
                <h2 className="text-2xl font-semibold mt-4">Your cart is empty</h2>
                <p className="text-gray-500 mt-2">Looks like you haven't added anything to your cart yet.</p>
                <Link to="/" className="mt-5">
                    <button className="px-6 py-3 bg-[#F4CF38] text-black font-bold rounded-lg transition flex align-items-center gap-2 m-0">
                        Continue Shopping <FaArrowRight/>
                    </button>
                </Link>
            </div>
        );
    }
    return (
        <div className="container mx-auto px-4">
        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-800 mt-5">YOUR CART</h2>
      
        {/* Clear Cart Button */}
        <div className="text-left mt-4">
          <button
            className="bg-red-500 text-white flex items-center px-4 py-2 rounded-lg shadow-md hover:bg-red-600 transition"
            onClick={handleDeleteCart}
          >
            <MdDelete className="mr-2 text-xl" /> Clear Cart
          </button>
        </div>
      
        {/* Cart Table */}
        <div className="mt-6 overflow-x-auto">
          <div className="w-full border rounded-lg shadow-md bg-white">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-gray-100 py-3 px-4 font-semibold text-gray-700">
              <div>PRODUCTS</div>
              <div>QUANTITY</div>
              <div>TOTAL</div>
            </div>
      
            {/* Cart Items */}
            {cartProducts.map(cartItem => (
              <div
                key={cartItem.id}
                className="grid grid-cols-3 items-center py-4 px-4 border-t"
              >
                {/* Product Info */}
                <div className="flex items-center gap-4">
                  <img
                    src={cartItem.products.productImage}
                    alt={cartItem.products.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <span className="text-gray-700 font-medium">{cartItem.products.name}</span>
                </div>
      
                {/* Quantity Controls */}
                <div className="flex items-center gap-3">
                  <button
                    className="px-3 py-1 bg-gray-200 rounded-full hover:bg-gray-300 transition"
                    onClick={() => decrement(cartItem.id, cartItem.quantity)}
                  >
                    -
                  </button>
                  <span className="text-lg font-semibold">{cartItem.quantity.toString().padStart(2, '0')}</span>
                  <button
                    className="px-3 py-1 bg-gray-200 rounded-full hover:bg-gray-300 transition"
                    onClick={() => increment(cartItem.id, cartItem.quantity)}
                  >
                    +
                  </button>
      
                  <button
                    className="text-red-500 hover:text-red-700 transition"
                    onClick={() => handleDeleteCartById(cartItem.id)}
                  >
                    <MdDelete className="text-xl" />
                  </button>
                </div>
      
                {/* Total Price */}
                <div className="text-gray-700 font-semibold text-lg">
                  ${cartItem.products.price}
                </div>
              </div>
            ))}
          </div>
        </div>
      
        {/* Subtotal & Checkout */}
        <div className="mt-6 flex justify-end">
          <div className="bg-gray-100 p-5 rounded-lg shadow-md w-full max-w-sm">
            <div className="flex justify-between text-gray-800 font-semibold text-lg">
              <span>SUBTOTAL</span>
              <span>${cartProductsTotal}</span>
            </div>
      
            <Link to={'/checkout'}>
              <button className="w-full mt-4 bg-black text-white py-3 rounded-lg shadow-md hover:bg-gray-800 transition">
                Checkout
              </button>
            </Link>
      
            <p className="text-sm text-gray-500 mt-2">Taxes and Shipping calculated at checkout</p>
          </div>
        </div>
      </div>
      
    );
}

export default Main;
